import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { clientsApi, type ClientDto, type UpdateClientRequest } from "@/lib/clients-accounts";

export interface JobClientTabProps {
  jobId: number;
  clientId: number | null;
}

export function JobClientTab({ jobId, clientId }: JobClientTabProps) {
  // Hook must run unconditionally — gate with `enabled` instead of an early return.
  const clientQuery = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.get(clientId!),
    enabled: clientId != null,
  });

  if (clientId == null) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No client linked to this record.
        </CardContent>
      </Card>
    );
  }

  if (clientQuery.isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Loading…
        </CardContent>
      </Card>
    );
  }
  if (clientQuery.isError || !clientQuery.data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          Failed to load client.
        </CardContent>
      </Card>
    );
  }

  return <JobClientTabBody jobId={jobId} client={clientQuery.data} />;
}

interface ClientDraft {
  firstName: string; lastName: string;
  companyName: string; departmentName: string;
  phone: string; mobilePhone: string; fax: string;
  emailAddress: string; ccEmailAddress: string;
  branch: string;
  hasApprovedCreditAccount: boolean;
  hasWebSiteLogin: boolean;
  plainPackaging: boolean;
}

function draftFromClient(c: ClientDto): ClientDraft {
  return {
    firstName: c.firstName, lastName: c.lastName,
    companyName: c.companyName ?? "", departmentName: c.departmentName ?? "",
    phone: c.phone ?? "", mobilePhone: c.mobilePhone ?? "", fax: c.fax ?? "",
    emailAddress: c.emailAddress ?? "", ccEmailAddress: c.ccEmailAddress ?? "",
    branch: c.branch ?? "",
    hasApprovedCreditAccount: c.hasApprovedCreditAccount,
    hasWebSiteLogin: c.hasWebSiteLogin,
    plainPackaging: c.plainPackaging,
  };
}

function buildUpdateRequest(c: ClientDto, d: ClientDraft): UpdateClientRequest {
  return {
    firstName: d.firstName, lastName: d.lastName,
    emailAddress: d.emailAddress || null,
    ccEmailAddress: d.ccEmailAddress || null,
    phone: d.phone || null,
    fax: d.fax || null,
    mobilePhone: d.mobilePhone || null,
    companyId: c.companyId,
    companyName: d.companyName || null,
    departmentName: d.departmentName || null,
    branch: d.branch || null,
    lockCompanyName: c.lockCompanyName,
    showEmail: c.showEmail,
    plainPackaging: d.plainPackaging,
    summary: c.summary,
    deliveryAddressId: c.deliveryAddressId,
    invoiceAddressId: c.invoiceAddressId,
    hasApprovedCreditAccount: d.hasApprovedCreditAccount,
    hasWebSiteLogin: d.hasWebSiteLogin,
  };
}

function JobClientTabBody({ jobId, client }: { jobId: number; client: ClientDto }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ClientDraft>(() => draftFromClient(client));

  // Sync draft when source client changes (e.g. after refetch following save).
  // Gate by !editing so an in-flight edit isn't clobbered.
  useEffect(() => {
    if (!editing) setDraft(draftFromClient(client));
  }, [client, editing]);

  const saveMut = useMutation({
    mutationFn: (body: UpdateClientRequest) => clientsApi.patch(client.clientId, body),
    onSuccess: () => {
      toast.success("Client saved");
      void qc.invalidateQueries({ queryKey: ["client", client.clientId] });
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  const handleCancel = () => { setDraft(draftFromClient(client)); setEditing(false); };
  const handleSave   = () => { saveMut.mutate(buildUpdateRequest(client, draft)); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
            <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">Company Details</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <dl className="space-y-3">
              <EditableRow label="Company"    value={draft.companyName}    editing={editing}
                           onChange={(v) => setDraft({ ...draft, companyName: v })} />
              <EditableRow label="Department" value={draft.departmentName} editing={editing}
                           onChange={(v) => setDraft({ ...draft, departmentName: v })} />
              <BoolRow label="Existing Account"    checked={draft.hasApprovedCreditAccount} editing={editing}
                       onChange={(v) => setDraft({ ...draft, hasApprovedCreditAccount: v })} />
              <BoolRow label="Use Plain Packaging" checked={draft.plainPackaging}           editing={editing}
                       onChange={(v) => setDraft({ ...draft, plainPackaging: v })} />
            </dl>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
            <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <dl className="space-y-3">
              <EditableRow label="First Name"       value={draft.firstName}      editing={editing}
                           onChange={(v) => setDraft({ ...draft, firstName: v })} />
              <EditableRow label="Last Name"        value={draft.lastName}       editing={editing}
                           onChange={(v) => setDraft({ ...draft, lastName: v })} />
              <EditableRow label="Phone"            value={draft.phone}          editing={editing}
                           onChange={(v) => setDraft({ ...draft, phone: v })} />
              <EditableRow label="Mobile"           value={draft.mobilePhone}    editing={editing}
                           onChange={(v) => setDraft({ ...draft, mobilePhone: v })} />
              <EditableRow label="Fax"              value={draft.fax}            editing={editing}
                           onChange={(v) => setDraft({ ...draft, fax: v })} />
              <EditableRow label="Email Address"    value={draft.emailAddress}   editing={editing}
                           onChange={(v) => setDraft({ ...draft, emailAddress: v })} />
              <EditableRow label="CC Email Address" value={draft.ccEmailAddress} editing={editing}
                           onChange={(v) => setDraft({ ...draft, ccEmailAddress: v })} />
              <EditableRow label="Branch"           value={draft.branch}         editing={editing}
                           onChange={(v) => setDraft({ ...draft, branch: v })} />
              <BoolRow label="Website" checked={draft.hasWebSiteLogin} editing={editing}
                       onChange={(v) => setDraft({ ...draft, hasWebSiteLogin: v })} />
              <ReadOnlyRow label="Created On" value={formatDate(client.createdOn)} />
              <ReadOnlyRow label="Created By" value={client.createdByName ?? "n/a"} />
            </dl>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2 border-t border-border pt-4">
        {editing ? (
          <>
            <Button variant="outline" onClick={handleCancel} disabled={saveMut.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
        )}
      </div>
    </div>
  );
}

function EditableRow({
  label, value, editing, onChange,
}: { label: string; value: string; editing: boolean; onChange: (v: string) => void }) {
  const id = `client-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm items-center">
      <Label htmlFor={id} className="text-muted-foreground">{label}</Label>
      {editing
        ? <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
        : <dd>{value || "n/a"}</dd>}
    </div>
  );
}

function BoolRow({
  label, checked, editing, onChange,
}: { label: string; checked: boolean; editing: boolean; onChange: (v: boolean) => void }) {
  const id = `client-bool-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm items-center">
      <Label htmlFor={id} className="text-muted-foreground">{label}</Label>
      {editing
        ? <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
        : <dd>{checked ? "Yes" : "No"}</dd>}
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm items-center">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "n/a";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "n/a";
  return d.toLocaleDateString("en-AU");
}
